import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Shield, ShieldAlert, ShieldCheck, ShieldX, Sparkles } from "lucide-react";
import StatusBadge from "@/components/app/StatusBadge";
import { Button } from "@/components/ui/button";

const BUCKETS = [
  { key: "under_warranty", label: "Under warranty", tone: "green", icon: ShieldCheck, valueKey: "under_warranty" },
  { key: "expiring_30", label: "Expiring ≤ 30 days", tone: "rose", icon: ShieldAlert, valueKey: "expiring_30" },
  { key: "expiring_60", label: "Expiring 31 – 60 days", tone: "amber", icon: ShieldAlert, valueKey: "expiring_60" },
  { key: "expiring_90", label: "Expiring 61 – 90 days", tone: "amber", icon: ShieldAlert, valueKey: "expiring_90" },
  { key: "out_of_warranty", label: "Out of warranty", tone: "slate", icon: ShieldX, valueKey: "out_of_warranty" },
  { key: "applecare", label: "AppleCare active", tone: "blue", icon: Sparkles, valueKey: "applecare_active" },
];

const TONE_CLASS = {
  green: "bg-emerald-50 border-emerald-200 text-emerald-700",
  amber: "bg-amber-50 border-amber-200 text-amber-800",
  rose: "bg-rose-50 border-rose-200 text-rose-700",
  slate: "bg-slate-50 border-slate-200 text-slate-700",
  blue: "bg-sky-50 border-sky-200 text-sky-700",
};

export default function Warranty() {
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState("expiring_30");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/warranty/summary").then(({ data }) => setSummary(data)); }, []);
  useEffect(() => {
    setLoading(true);
    api.get("/warranty/list", { params: { bucket: selected, limit: 200 } })
      .then(({ data }) => setItems(data.items || []))
      .finally(() => setLoading(false));
  }, [selected]);

  return (
    <div className="space-y-5 max-w-[1400px]">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Intelligence · Coverage</div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight">Warranty Intelligence</h1>
        <p className="text-sm text-slate-500 mt-0.5">Coverage state of every active device — plan repairs, refresh and AppleCare renewals.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {BUCKETS.map((b) => {
          const count = summary?.[b.valueKey] ?? 0;
          const active = selected === b.key;
          const Icon = b.icon;
          return (
            <button
              key={b.key}
              onClick={() => setSelected(b.key)}
              data-testid={`warranty-tile-${b.key}`}
              className={`text-left rounded-md border p-4 transition-all ${TONE_CLASS[b.tone]} ${active ? "ring-2 ring-slate-900 ring-offset-2" : "hover:brightness-95"}`}
            >
              <div className="flex items-center justify-between">
                <Icon className="h-4 w-4" strokeWidth={1.5} />
                <div className="text-[10px] font-semibold tracking-[0.08em] uppercase opacity-70">{active ? "Viewing" : ""}</div>
              </div>
              <div className="mt-3 text-2xl font-heading font-bold tabular-nums">{count.toLocaleString()}</div>
              <div className="mt-0.5 text-xs">{b.label}</div>
            </button>
          );
        })}
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <FactTile label="Unknown warranty" value={summary.unknown} hint="Missing warranty end date — recommend enrichment via vendor" />
          <FactTile label="AppleCare expired" value={summary.applecare_expired} hint="Devices that had AppleCare but coverage ended" />
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">Top brands out of warranty</div>
            <div className="mt-2 space-y-1.5">
              {(summary.top_out_brands || []).slice(0, 5).map((b) => (
                <div key={b.key} className="flex items-center justify-between text-sm">
                  <span className="text-slate-700">{b.key}</span>
                  <span className="tabular-nums font-medium text-slate-800">{b.count}</span>
                </div>
              ))}
              {(!summary.top_out_brands || summary.top_out_brands.length === 0) && <div className="text-xs text-slate-500">No data</div>}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-800">Devices — {BUCKETS.find((b) => b.key === selected)?.label}</div>
            <div className="text-xs text-slate-500">{items.length} results</div>
          </div>
          <Shield className="h-4 w-4 text-slate-400" strokeWidth={1.5} />
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-2.5">Device</th>
              <th className="text-left px-4 py-2.5">Serial</th>
              <th className="text-left px-4 py-2.5">Warranty end</th>
              <th className="text-left px-4 py-2.5">AppleCare</th>
              <th className="text-left px-4 py-2.5">Lifecycle</th>
              <th className="text-left px-4 py-2.5">Country</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No devices in this bucket</td></tr>
            ) : items.map((a) => (
              <tr key={a.id} className="hover:bg-slate-50">
                <td className="px-4 py-2.5">
                  <div className="font-medium text-slate-800">{a.brand} {a.model}</div>
                  <div className="text-xs text-slate-500 font-mono">{a.asset_tag}</div>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-700">{a.serial}</td>
                <td className="px-4 py-2.5 text-slate-700 tabular-nums">{a.warranty_end ? new Date(a.warranty_end).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-2.5 text-slate-700 tabular-nums">{a.applecare_end ? new Date(a.applecare_end).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-2.5"><StatusBadge value={a.lifecycle_state} /></td>
                <td className="px-4 py-2.5 text-slate-700">{a.country || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FactTile({ label, value, hint }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-heading font-bold text-slate-900 tabular-nums">{(value ?? 0).toLocaleString()}</div>
      <div className="mt-1 text-[11px] text-slate-500 leading-relaxed">{hint}</div>
    </div>
  );
}
